/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { DirectoryMolecule } from "./Directory.molecule";
import type { DirectoryConfig } from "./core/Directory.types";
import { DirectoryFilters } from "./filters/DirectoryFilters.molecule";
import { DirectoryGrid } from "./grid/DirectoryGrid.molecule";
import { DirectoryHeading } from "./chrome/DirectoryHeading.element";
import { DirectoryNoResults } from "./grid/DirectoryNoResults.element";
import { DirectoryPagination } from "./chrome/DirectoryPagination.molecule";
import { DirectoryResultsCount } from "./chrome/DirectoryResultsCount.element";
import { DirectorySection } from "./chrome/DirectorySection.element";

export type DirectoryProps<T> = DirectoryConfig<T> & { children: React.ReactNode };

/**
 * Generalized searchable / filterable / paginated grid with URL state.
 *
 * See `libraries/cosmos-components/src/elements/standalone-sections/directory/README.md` for
 * usage patterns, filter-kind decisions, and migration sketches.
 *
 * ## Composition
 *
 * `Directory.Section` is a chrome wrapper (ResponsiveContainer + SectionContainer)
 * that does NOT read context; render it OUTSIDE `<Directory>` to frame the
 * section. `Directory.Heading` is also context-free — typically rendered
 * inside `<Directory>` so the heading sits above the filter row, but it
 * works either side. Every other slot (`Filters`, `Grid`, `NoResults`,
 * `Pagination`) reads from Directory context and MUST be a descendant of
 * `<Directory>`.
 *
 * ## Generic propagation
 *
 * Static properties on a generic function lose the parent's `<T>` — there's
 * no way for `Directory.Grid` to inherit `<T>` from the enclosing
 * `<Directory<T>>` automatically. Re-state the type on the slot:
 *
 *   <Directory.Section>
 *     <Directory<Instructor> data={...} getKey={...} paramPrefix="inst" filters={...}>
 *       <Directory.Heading eyebrow="..." title="..." />
 *       <Directory.Filters />
 *       <Directory.Grid<Instructor>>       // <- repeat <Instructor> here
 *         {(inst) => <InstructorCard data={inst} />}
 *       </Directory.Grid>
 *       <Directory.Pagination />
 *     </Directory>
 *   </Directory.Section>
 *
 * Only `Grid` requires the generic restate; the others read from context.
 */
function DirectoryRoot<T>(props: DirectoryProps<T>): React.JSX.Element {
	return <DirectoryMolecule {...props} />;
}

// Attach compound slots. Typed as a generic function with static properties so JSX
// generics work at the root level while slots remain accessible.
//
// Composition rules (also see the docstring above):
//   * Section, Heading  — context-FREE chrome; can render outside `<Directory>`.
//   * Filters, ResultsCount, NoResults, Pagination — read from Directory context; must be
//     descendants of `<Directory>`.
//   * Grid — context-coupled AND generic-required (re-state `<T>` at the slot).
//
// `DirectorySpotlight` is intentionally NOT attached here — it composes
// standalone (outside the `<Directory>` root), so making it a compound slot
// only invites consumers to write code that fails at runtime. Import it
// directly from `@cosmos/components` as `DirectorySpotlight`.
type DirectoryComponent = {
	<T>(props: DirectoryProps<T>): React.JSX.Element;
	Section: typeof DirectorySection;
	Heading: typeof DirectoryHeading;
	Filters: typeof DirectoryFilters;
	ResultsCount: typeof DirectoryResultsCount;
	Grid: typeof DirectoryGrid;
	NoResults: typeof DirectoryNoResults;
	Pagination: typeof DirectoryPagination;
};

const Directory: DirectoryComponent = Object.assign(DirectoryRoot, {
	Section: DirectorySection,
	Heading: DirectoryHeading,
	Filters: DirectoryFilters,
	ResultsCount: DirectoryResultsCount,
	Grid: DirectoryGrid,
	NoResults: DirectoryNoResults,
	Pagination: DirectoryPagination,
});

export { Directory };
